import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Dropdown from '../../components/dropdown';
import InputField from '../../components/formItem/inputField';
import { SortableHeaderCell, HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { Prev, Next } from '../../assets/images';
import { formatDateTime, createSortConfig, handleSort, toDisplayString } from '../../utils/base';
import { ORGANIZATION_TYPES, API_PAGE_SIZE } from '../../constants';
import { organizationAPI, userAPI } from '../../actions/imsServer';
import StatusIndicator from '../../components/statusIndicator';

const ApplicationList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [filterValue, setFilterValue] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // 검색 필드 상태
    const [searchFields, setSearchFields] = useState({
        organizationName: ''
    });

    // 정렬 상태 (초기값: createDate 내림차순 - 최신 신청일순)
    const [sortConfig, setSortConfig] = useState(createSortConfig('createDate', 'desc'));
    const hasLoadedOnce = useRef(false);

    // API에서 기관 목록 가져오기
    const fetchOrganizations = useCallback(async (searchParams = {}, showLoading = true, page = 1, currentSortConfig = null, currentFilterValue = filterValue, currentSearchFields = searchFields) => {
        // currentSortConfig가 없으면 기본값 사용
        const sortConfigToUse = currentSortConfig || sortConfig;
        try {
            if (showLoading) {
                setLoading(true);
            } else {
                // 필터/정렬 시에는 refreshing 상태만 설정 (테이블은 유지)
                setRefreshing(true);
            }
            setError(null);

            try {
                // 검색 파라미터 준비
                const offset = (page - 1) * ITEMS_PER_PAGE;

                // 필터에 따라 state 파라미터 설정
                let stateValue = '2,3,4,5'; // 기본값: 전체
                if (currentFilterValue === 'all_excluding_deleted') {
                    stateValue = '2,3,4';
                } else if (currentFilterValue === 'pending_approval') {
                    stateValue = '2';
                } else if (currentFilterValue === 're_pending_approval') {
                    stateValue = '3';
                } else if (currentFilterValue === 'all') {
                    stateValue = '2,3,4,5';
                }

                const apiParams = {
                    state: stateValue,
                    expand: 'memberships,didims,user',
                    offset: offset,
                    limit: ITEMS_PER_PAGE,
                    ...searchParams
                };

                // 서버사이드 정렬 파라미터 추가
                // 초기화면은 최초 신청날짜(createDate) 기준으로 내림차순 정렬
                if (sortConfigToUse.key) {
                    apiParams.sort = sortConfigToUse.key;
                    apiParams.order = sortConfigToUse.direction === 'asc' ? 'asc' : 'desc';
                } else {
                    // 정렬이 설정되지 않은 경우 기본값: createDate 내림차순
                    apiParams.sort = 'createDate';
                    apiParams.order = 'desc';
                }

                // 검색 파라미터 포함하여 API 호출
                const organizationsResponse = await organizationAPI.getOrganizations(apiParams);
                let allOrganizations = organizationsResponse?.data?.organizations || organizationsResponse?.organizations || [];
                const total = organizationsResponse?.data?.total ?? allOrganizations?.length ?? 0;
                setTotalCount(total);

                // allOrganizations에서 userId 추출
                const userIds = allOrganizations
                    .map(org => org.userId)
                    .filter(userId => userId != null);

                // userId로 사용자 정보 조회
                let userMap = new Map();
                if (userIds.length > 0) {
                    try {
                        const userIdsParam = userIds.join(',');
                        const usersResponse = await userAPI.getUsers('all', {
                            userId: userIdsParam,
                            state: 0,
                            limit: userIds.length
                        });
                        const users = usersResponse?.data?.users || usersResponse?.users || [];
                        users.forEach(user => {
                            if (user.id) {
                                userMap.set(user.id, user);
                            }
                        });
                    } catch (err) {
                        console.error('Failed to fetch users:', err);
                    }
                }

                // 기관별 디딤 ID 계산 및 추가 정보 설정
                // expand로 받아온 didims 데이터를 우선 사용
                let organizationsWithData = allOrganizations.map(org => {
                    // expand로 받아온 didims 사용 (배열인지 확인 후 처리)
                    const orgDidims = Array.isArray(org.didims) ? org.didims : [];
                    // didims 배열에서 id들을 추출
                    const didimIds = orgDidims.map(didim => didim.id || didim).filter(id => id != null);

                    // 관리자 정보 (userId로 userMap에서 조회)
                    const adminUser = org.userId ? userMap.get(org.userId) : null;
                    const adminName = adminUser?.name || adminUser?.userName || '';

                    return {
                        ...org,
                        didimIds: didimIds,
                        adminName: adminName,
                        // 상태 업데이트 날짜 (lastDate)
                        lastDate: org.lastDate || null,
                        // 반려메모 (stateInfo)
                        stateInfo: org.stateInfo || null
                    };
                });

                setOrganizations(organizationsWithData);

            } catch (err) {
                console.error('Failed to fetch organizations:', err);
            }

        } catch (err) {
            console.error('Failed to fetch organizations:', err);
            setError(t('failed_to_load_organization_list'));
        } finally {
            if (showLoading) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    }, [sortConfig, filterValue, searchFields, t]);

    // 검색 파라미터 생성 함수
    const getSearchParams = useCallback(() => {
        const params = {};
        // 기관명 검색
        if (searchFields.organizationName) {
            params.keyword = searchFields.organizationName;
        }
        return params;
    }, [searchFields.organizationName]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
        const searchParams = getSearchParams();
        fetchOrganizations(searchParams, true, newPage, sortConfig, filterValue, searchFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [totalCount, getSearchParams, sortConfig, filterValue, searchFields]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, totalCount, handlePageChange]);

    // 컴포넌트 마운트 및 필터/정렬 변경 시에만 데이터 로드 (첫 로딩만 전체 로딩, 이후에는 테이블 유지하며 갱신)
    useEffect(() => {
        setOrganizations([]);
        setCurrentPage(1);
        setTotalCount(0);
        const showLoading = !hasLoadedOnce.current;
        fetchOrganizations({}, showLoading, 1, sortConfig, filterValue, searchFields).finally(() => {
            hasLoadedOnce.current = true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [sortConfig, filterValue]);

    // 검색 실행 함수
    const handleSearch = useCallback(async () => {
        setCurrentPage(1);
        const searchParams = getSearchParams();
        fetchOrganizations(searchParams, false, 1, sortConfig, filterValue, searchFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [getSearchParams, sortConfig, filterValue, searchFields]);

    // 기관명 입력 후 디바운스로 자동 조회 (검색 필터 선택 시에만)
    useEffect(() => {
        if (filterValue !== 'search_by_organization_name') return;
        const keyword = (searchFields.organizationName || '').trim();
        const timer = setTimeout(() => {
            setCurrentPage(1);
            const searchParams = keyword ? { keyword } : {};
            fetchOrganizations(searchParams, false, 1, sortConfig, filterValue, searchFields);
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [searchFields.organizationName, filterValue]);

    // 정렬 함수
    const onSort = (key) => {
        const newSortConfig = handleSort(sortConfig, key);
        setSortConfig(newSortConfig);
        // 정렬 시 첫 페이지로 이동하고 데이터 다시 로드
        setCurrentPage(1);
        const searchParams = getSearchParams();
        fetchOrganizations(searchParams, false, 1, newSortConfig, filterValue, searchFields);
    };

    // 상세 페이지로 이동 핸들러
    const handleViewDetail = (organization) => {
        navigate('/applications/detail', {
            state: {
                organization: organization
            }
        });
    };

    // 상태 텍스트와 색상 매핑 함수
    const getStatusInfo = (state) => {
        // state 값에 따라 상태 텍스트와 색상 반환
        // 2: 승인대기, 3: 재승인대기, 4: 반려, 5: 삭제
        const stateNum = Number(state);

        if (stateNum === 2) {
            return { text: t('pending_approval'), color: theme.palette.figma.steps };
        } else if (stateNum === 3) {
            return { text: t('re_approval_pending'), color: theme.palette.figma.yellowText };
        } else if (stateNum === 4) {
            return { text: t('rejected'), color: theme.palette.figma.Red_didim };
        } else if (stateNum === 5) {
            return { text: t('delete'), color: theme.palette.figma.white[30] };
        } else {
            return { text: t('pending_approval'), color: theme.palette.figma.steps };
        }
    };

    return (
        <ContentArea>
            {/* 로딩 상태 */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography>{t('loading_organization_list')}</Typography>
                </Box>
            )}

            {/* 에러 상태 */}
            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* 검색 영역 */}
            {!loading && (
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    px: 2.5,
                    py: 2.5,
                    mb: 3,
                    gap: 2,
                    alignItems: 'center'
                }}>
                    <Dropdown
                        options={[
                            { value: 'all', label: 'all' },
                            { value: 'all_excluding_deleted', label: 'all_excluding_deleted' },
                            { value: 'pending_approval', label: 'pending_approval' },
                            { value: 're_pending_approval', label: 're_approval_pending' },
                            { value: 'search_by_organization_name', label: 'search_by_organization_name' },
                        ]}
                        value={filterValue}
                        onChange={(option) => {
                            setFilterValue(option.value);
                            setCurrentPage(1);
                            setTimeout(() => {
                                const searchParams = getSearchParams();
                                fetchOrganizations(searchParams, false, 1, sortConfig, option.value, searchFields);
                            }, 0);
                        }}
                        placeholder={t('select_filter')}
                    />
                    {filterValue === 'search_by_organization_name' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            <InputField
                                type="text"
                                placeholder={t('please_enter_organization_name')}
                                value={searchFields.organizationName}
                                onChange={(e) => setSearchFields(prev => ({ ...prev, organizationName: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                                showClearIcon={true}
                                onClear={() => {
                                    setSearchFields(prev => ({ ...prev, organizationName: '' }));
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* 테이블 */}
            {!loading && !error && (
                <Box
                    sx={{
                        px: 2.5,
                        mb: 3,
                        opacity: 1,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: refreshing ? 'none' : 'auto',
                    }}
                >
                    <TableContainer component={Paper}>
                        <Table aria-label="organization table">
                            <TableHead>
                                <TableRow>
                                    <SortableHeaderCell
                                        columnKey="createDate"
                                        label={t('initial_application_date')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="name"
                                        label={t('organization_name')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="country"
                                        label={t('country')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="type"
                                        label={t('organization_type')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell label={t('didim_h_w_connection')} align="center" />
                                    <SortableHeaderCell columnKey="lastDate" label={t('status_update')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <HeaderCell label={t('administrator_name')} align="center" />
                                    <SortableHeaderCell columnKey="stateInfo" label={t('rejection_memo')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell
                                        columnKey="state"
                                        label={t('status')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.map((org) => {
                                    const statusInfo = getStatusInfo(org.state);
                                    return (
                                        <TableRow
                                            key={org.id}
                                            onClick={() => handleViewDetail(org)}
                                            sx={{
                                                cursor: 'pointer',
                                                ...getTableStyles(theme).tableRowStyle
                                            }}
                                        >
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {formatDateTime(org.createDate, '/', ':', 2, 19)}
                                            </TableCell>
                                            <TableCell>{org.name}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {org.country === 'ko' ? t('south_korea') : org.country || '-'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {(() => { const typ = ORGANIZATION_TYPES.find(typeItem => typeItem.value === org.type); return typ ? toDisplayString(t(typ.label), typ.label) : '-'; })()}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {org.didimIds && org.didimIds.length > 0
                                                    ? org.didimIds.join(', ')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {org.lastDate ? formatDateTime(org.lastDate, '/', ':', 2, 19) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {org.adminName || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {(org.stateInfo && JSON.parse(org.stateInfo).memo) || '-'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <StatusIndicator
                                                    text={statusInfo.text}
                                                    sx={{ backgroundColor: statusInfo.color }}
                                                    textColor="#000000"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {totalCount > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma.white[80]} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma.white[80],
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma.white[80]} size={20} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

        </ContentArea>
    );
};

export default ApplicationList;
